/**
 * Patient-related GraphQL queries
 * Consolidated from COMMON_QUERIES and GRAPHQL_QUERIES
 */

export const PATIENT_QUERIES = {
    // Patient data retrieval
    GET_CONSOLIDATED_DATA: `
    query GetConsolidatedData($where: pep_vw_consolidated_data_bool_exp, $orderBy: [pep_vw_consolidated_data_order_by!], $limit: Int, $offset: Int) {
      pep_vw_consolidated_data(where: $where, order_by: $orderBy, limit: $limit, offset: $offset) {
        patient_clinic_data_id
        full_name
        dob
        emr_id
        provider_name
        primary_dx
        secondary_dx
        clinic_id
        facility_id
        provider_id
        ccm_status
        rpm_status
        has_active_group
        search_string
        mobile_phone_number
        home_phone_number
        primary_insurance_provider
        secondary_insurance_provider
        insurance_type
        last_seen_date
        pcm_flag
        ccm_flag
        rpm_flag
      }
    }
  `,

    GET_PATIENT_DATA: `
    query GetPatientData($patientId: Int!) {
      pep_vw_consolidated_data(where: {patient_clinic_data_id: {_eq: $patientId}}) {
        patient_clinic_data_id
        first_name
        last_name
        dob
        gender
        mobile_phone_number
        home_phone_number
        email
        address_line_1
        city
        state
        zip_code
        clinic_name
        provider_name
        primary_dx
        secondary_dx
        primary_insurance_provider
        primary_insurance_id
        secondary_insurance_provider
        secondary_insurance_id
        emr_id
        program_eligibility
      }
    }
  `,

    GET_PATIENT_CONSOLIDATED_DATA: `
    query GetPatientConsolidatedData($patientId: Int!) {
      pep_vw_consolidated_data(where: {patient_clinic_data_id: {_eq: $patientId}}) {
        first_name
        last_name
        dob
        gender
        mobile_phone_number
        home_phone_number
        email
        address_line_1
        city
        state
        state_id
        zip_code
        clinic_name
        facility_id
        facility_name
        emr_id
        provider_id
        provider_name
        primary_dx
        primary_dx_id
        secondary_dx
        secondary_dx_id
        primary_insurance_provider
        primary_insurance_id
        secondary_insurance_provider
        secondary_insurance_id
        rpm_flag
        ccm_flag
        pcm_flag
        pep_patient_id
        patient_clinic_data_id
        patient_clinic_timezone
        ccm_status
        rpm_status
        ccm_appointment_dt
        rpm_appointment_dt
        rpm_appointment_facility
        ccm_vitals
        clinic_id
        ccm_status_updates
        emergency_first_name
        emergency_last_name
        emergency_mobile_phone
      }
    }
  `,

    GET_PATIENTS_BY_EMR_IDS: `
    query GetPatientsByEmrIds($emrIds: [String!]!, $clinicId: Int!) {
      pep_vw_consolidated_data(
        where: {
          emr_id: {_in: $emrIds},
          clinic_id: {_eq: $clinicId}
        }
      ) {
        patient_clinic_data_id
        full_name
        dob
        mobile_phone_number
        clinic_name
        provider_name
        emr_id
      }
    }
  `,

    GET_SMS_PATIENT_DATA: `
    query GetSMSPatientData($patientId: Int!) {
      pep_vw_consolidated_data(where: {patient_clinic_data_id: {_eq: $patientId}}) {
        full_name
        mobile_phone_number
        clinic_name
        provider_name
        patient_clinic_timezone
      }
    }
  `,

    // Patient details (from GRAPHQL_QUERIES)
    GET_PATIENT_DETAILS: `
    query GetPatientDetails($patientClinicDataId: Int!) {
      pep_vw_consolidated_data(where: {patient_clinic_data_id: {_eq: $patientClinicDataId}}) {
        patient_clinic_data_id
        pep_patient_id
        first_name
        last_name
        dob
        gender
        mobile_phone_number
        home_phone_number
        email
        address_line_1
        city
        state
        zip_code
        primary_insurance_id
        secondary_insurance_id
        primary_insurance_provider
        secondary_insurance_provider
        emr_id
        provider_name
        provider_id
        clinic_name
        clinic_id
        state_id
        primary_dx
        secondary_dx
        ccm_flag
        rpm_flag
        pcm_flag
        ccm_status_value
        rpm_status_value
        patient_clinic_timezone
        emergency_first_name
        emergency_last_name
        emergency_mobile_phone
        rpm_appointment_dt
      }
    }
  `,

    // ES Patient data using assigned_user_id from workflow assignments view
    ES_PATIENT_DATA: `
    query GetESPatientData($userId: Int!) {
      pep_vw_patient_workflow_user_assignments(
        where: {assigned_user_id: {_eq: $userId}}
        limit: 1
        distinct_on: patient_id
      ) {
        activity_id
        allocation_name
        assigned_user_id
        assigned_user_type
        attempt
        current_program_status_id
        current_task_id
        current_workflow_attempt
        current_workflow_status_id
        dob
        emr_id
        patient_full_name
        patient_id
        patient_program_id
        program_status_mapper_id
        program_type_id
        task_id
        user_group_id
        user_group_name
        wait_time
        workflow_attempt_config_id
        workflow_id
        workflow_name
        workflow_program_type_mapper_id
        workflow_progress_mapper_id
        appointment_id
        clinic_name
        program_type
      }
      pep_vw_patient_workflow_user_assignments_aggregate(
        where: {assigned_user_id: {_eq: $userId}}
        distinct_on: patient_id
      ) {
        aggregate {
          count
        }
      }
    }
  `,

    // Patient assignment by userId (for external navigation to VPE)
    PATIENT_ASSIGNMENT_BY_PATIENT_ID: `
    query GetPatientAssignmentByPatientId($userId: Int!) {
      pep_vw_patient_workflow_user_assignments(
        where: {assigned_user_id: {_eq: $userId}}
        limit: 1
        distinct_on: patient_id
      ) {
        activity_id
        allocation_name
        assigned_user_id
        assigned_user_type
        attempt
        current_program_status_id
        current_task_id
        current_workflow_attempt
        current_workflow_status_id
        dob
        emr_id
        patient_full_name
        patient_id
        patient_program_id
        program_status_mapper_id
        program_type_id
        task_id
        user_group_id
        user_group_name
        wait_time
        workflow_attempt_config_id
        workflow_id
        workflow_name
        workflow_program_type_mapper_id
        workflow_progress_mapper_id
        appointment_id
        clinic_name
        program_type
      }
    }
  `,

    // Care Specialist Patient Lists
    GET_CS_CONSENT_PATIENTS: `
    query GetCSConsentPatients($clinicIds: [Int!]!) {
      pep_vw_care_specialist_patient_list(
        where: {clinic_id: {_in: $clinicIds}},
        order_by: {consented_appointment_date_time: desc_nulls_last}
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        clinic_id
        clinic_name
        patient_clinic_timezone
        consented_appointment_date_time
        dob
        provider_name
        mobile_phone_number
        ccm_status_value
        rpm_status_value
      }
    }
  `,

    GET_CS_FOLLOWUP_PATIENTS: `
    query GetCSFollowUpPatients {
      pep_vw_cs_task_patient_list(
        where: {ccm_status_value: {_eq: "Follow Up"}},
        order_by: {consented_appointment_date_time: desc_nulls_last}
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        clinic_id
        clinic_name
        patient_clinic_timezone
        consented_appointment_date_time
        dob
        provider_name
        mobile_phone_number
        ccm_status_value
        rpm_status_value
      }
    }
  `,

    GET_CS_DEFERRED_PATIENTS: `
    query GetCSDeferredPatients {
      pep_vw_cs_task_patient_list(
        where: {ccm_status_value: {_eq: "Deferred (CS)"}},
        order_by: {consented_appointment_date_time: desc_nulls_last}
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        clinic_id
        clinic_name
        patient_clinic_timezone
        consented_appointment_date_time
        dob
        provider_name
        mobile_phone_number
        ccm_status_value
        rpm_status_value
      }
    }
  `,

    GET_CARE_SPECIALIST_PATIENT_LIST: `
    query GetCareSpecialistPatientList($clinicIds: [Int!]!) {
      pep_vw_care_specialist_patient_list(
        where: {clinic_id: {_in: $clinicIds}},
        order_by: {consented_appointment_date_time: desc_nulls_last}
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        clinic_id
        clinic_name
        patient_clinic_timezone
        consented_appointment_date_time
        dob
        provider_name
        mobile_phone_number
        ccm_status_value
        rpm_status_value
      }
    }
  `,

    // Patient Enroller Lists
    GET_PE_PATIENT_LIST: `
    query GetPEPatientList($clinicIds: [Int!]!) {
      pep_vw_patient_enroller_patient_list(where: {clinic_id: {_in: $clinicIds}}) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        dob
        clinic_id
        clinic_name
        provider_name
        patient_clinic_timezone
        phone_number
        consented_appointment_date_time
        ccm_status_value
        rpm_status_value
      }
    }
  `,

    GET_CS_TASK_PATIENT_LIST: `
    query GetCSTaskPatientList($whereClause: String!) {
      pep_vw_cs_task_patient_list(
        where: $whereClause,
        order_by: {consented_appointment_date_time: desc_nulls_last}
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        clinic_id
        clinic_name
        patient_clinic_timezone
        consented_appointment_date_time
        dob
        provider_name
        mobile_phone_number
        ccm_status_value
        rpm_status_value
      }
    }
  `,

    GET_PE_TASK_PATIENT_LIST: `
    query GetPETaskPatientList($clinicIds: [Int!]!, $statusFilter: String!) {
      pep_vw_patient_enroller_task_patient_list(
        where: {
          clinic_id: {_in: $clinicIds},
          rpm_status_value: {_eq: $statusFilter}
        },
        order_by: {consented_appointment_date_time: desc_nulls_last}
      ) {
        pep_patient_id
        patient_clinic_data_id
        full_name
        clinic_id
        clinic_name
        patient_clinic_timezone
        consented_appointment_date_time
        dob
        provider_name
        ccm_status_value
        rpm_status_value
      }
    }
  `,

  GET_PATIENT_DETAILS_FOR_GROUP: `
    query GetPatientDetailsForGroup($offset: Int, $limit: Int, $where: pep_vw_patient_details_bool_exp) {
      pep_vw_patient_details(offset: $offset, limit: $limit, where: $where) {
        enrollment_patient_id
        emr_patient_clinic_data_id
        emr_patient_master_id
        first_name
        last_name
        middle_name
        dob
        gender
        mobile_number
        home_number
        work_phone
        email
        address_line_one
        address_line_two
        city
        state
        zip_code
        emr_id
        emr_name
        clinic_id
        clinic_name
        clinic_facility_id
        clinic_facility_name
        provider_id
        provider_name
        patient_group_id
        created_by
        created_datetime
        updated_by
        updated_datetime
        identifier
        is_active
      }
      
      pep_vw_patient_details_aggregate(where: $where) {
        aggregate {
          count
        }
      }
    }
  `,

  GET_PRIMARY_DIAGNOSIS_BY_CLINIC: `
    query GetPrimaryDiagnosisByClinic($clinicID: Int!, $facilityIDs: [Int!], $providerIDs: [Int!]) {
      pep_vw_consolidated_data(
        where: {
          ccm_status: {_nin: [1,2,3,7,8,9,10]}, 
          clinic_id: {_eq: $clinicID},
          facility_id: {_in: $facilityIDs},
          provider_id: {_in: $providerIDs}
        }, 
        distinct_on: primary_dx
      ) {
        primary_dx
      }
    }
  `,

  GET_PATIENTS_BY_EMR_LIST: `
    query GetPatientsByEmrList($clinicId: Int!, $emrIds: [String!]!, $limit: Int, $offset: Int) {
      pep_vw_patient_details(
        limit: $limit
        offset: $offset
        where: {
          clinic_id: { _eq: $clinicId }
          emr_id: { _in: $emrIds }
          patient_group_id: { _is_null: true }
        }
      ) {
        enrollment_patient_id
        emr_patient_clinic_data_id
        emr_patient_master_id
        first_name
        last_name
        middle_name
        dob
        gender
        mobile_number
        home_number
        work_phone
        email
        address_line_one
        address_line_two
        city
        state
        zip_code
        emr_id
        emr_name
        clinic_id
        clinic_name
        clinic_facility_id
        clinic_facility_name
        provider_id
        provider_name
        patient_group_id
      }
      pep_vw_patient_details_aggregate(
        where: { clinic_id: { _eq: $clinicId }, emr_id: { _in: $emrIds }, patient_group_id: { _is_null: true } }
      ) {
        aggregate {
          count
        }
      }
    }
  `
};